// src/pages/Products.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { productsAPI, departmentAPI } from '../services/api';
import {
  Package,
  Plus,
  Edit2,
  Trash2,
  Search,
  AlertCircle,
  X,
  Upload,
  ImageIcon,
  Check,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { debounce } from 'lodash';
import { motion, AnimatePresence } from 'framer-motion';
import { CustomDropdown } from '../components/UI/CustomDropdown';

// --- Interfaces ---
interface Department {
  _id: string;
  name: string;
  nameEn?: string;
}

interface Product {
  _id: string;
  name: string;
  nameEn?: string;
  code: string;
  department: Department;
  price: number;
  unit?: string;
  unitEn?: string;
  image?: string;
  isActive: boolean;
  displayName: string;
  displayUnit: string;
}

// --- Translations ---
const translations = {
  ar: {
    manage: 'إدارة المنتجات',
    add: 'إضافة منتج جديد',
    addFirst: 'إضافة أول منتج',
    empty: 'لا توجد منتجات متاحة',
    noMatch: 'لا توجد منتجات مطابقة',
    searchPlaceholder: 'ابحث عن المنتجات...',
    filterDepartment: 'تصفية حسب القسم',
    allDepartments: 'كل الأقسام',
    activeProducts: 'المنتجات النشطة',
    inactiveProducts: 'المنتجات غير النشطة',
    name: 'اسم المنتج',
    nameEn: 'الاسم بالإنجليزية',
    code: 'رمز المنتج',
    department: 'القسم',
    price: 'السعر',
    unit: 'الوحدة',
    active: 'نشط',
    image: 'صورة المنتج',
    edit: 'تعديل',
    delete: 'حذف',
    namePlaceholder: 'أدخل اسم المنتج',
    nameEnPlaceholder: 'أدخل الاسم بالإنجليزية',
    codePlaceholder: 'أدخل رمز المنتج',
    pricePlaceholder: 'أدخل السعر',
    unitPlaceholder: 'اختر الوحدة',
    departmentPlaceholder: 'اختر القسم',
    imagePlaceholder: 'اختر صورة (اختياري)',
    update: 'تحديث المنتج',
    requiredFields: 'يرجى ملء جميع الحقول المطلوبة',
    invalidUnit: 'الوحدة غير صالحة، اختر من القائمة',
    invalidPrice: 'السعر يجب أن يكون رقمًا إيجابيًا',
    invalidImage: 'الصورة يجب أن تكون بصيغة JPG أو PNG',
    unauthorized: 'غير مصرح',
    fetchError: 'خطأ في جلب البيانات',
    saveError: 'خطأ في حفظ المنتج',
    deleteError: 'خطأ في حذف المنتج',
    added: 'تم إنشاء المنتج بنجاح',
    updated: 'تم تحديث المنتج بنجاح',
    deleted: 'تم حذف المنتج بنجاح',
    confirmDelete: 'تأكيد الحذف',
    deleteWarning: 'هل أنت متأكد من حذف هذا المنتج؟',
    cancel: 'إلغاء',
    toggleActive: 'تفعيل / إيقاف',
  },
  en: {
    manage: 'Manage Products',
    add: 'Add New Product',
    addFirst: 'Add First Product',
    empty: 'No products available',
    noMatch: 'No matching products',
    searchPlaceholder: 'Search products...',
    filterDepartment: 'Filter by department',
    allDepartments: 'All Departments',
    activeProducts: 'Active Products',
    inactiveProducts: 'Inactive Products',
    name: 'Product Name',
    nameEn: 'English Name',
    code: 'Product Code',
    department: 'Department',
    price: 'Price',
    unit: 'Unit',
    active: 'Active',
    image: 'Product Image',
    edit: 'Edit',
    delete: 'Delete',
    namePlaceholder: 'Enter product name',
    nameEnPlaceholder: 'Enter English name',
    codePlaceholder: 'Enter product code',
    pricePlaceholder: 'Enter price',
    unitPlaceholder: 'Select Unit',
    departmentPlaceholder: 'Select Department',
    imagePlaceholder: 'Choose image (optional)',
    update: 'Update Product',
    requiredFields: 'Please fill all required fields',
    invalidUnit: 'Invalid unit, please select from the list',
    invalidPrice: 'Price must be a positive number',
    invalidImage: 'Image must be JPG or PNG',
    unauthorized: 'Unauthorized',
    fetchError: 'Error fetching data',
    saveError: 'Error saving product',
    deleteError: 'Error deleting product',
    added: 'Product created successfully',
    updated: 'Product updated successfully',
    deleted: 'Product deleted successfully',
    confirmDelete: 'Confirm Deletion',
    deleteWarning: 'Are you sure you want to delete this product?',
    cancel: 'Cancel',
    toggleActive: 'Toggle Active',
  },
};

const unitOptions = [
  { value: 'كيلو', valueEn: 'Kilo', labelAr: 'كيلو', labelEn: 'Kilo' },
  { value: 'قطعة', valueEn: 'Piece', labelAr: 'قطعة', labelEn: 'Piece' },
  { value: 'علبة', valueEn: 'Pack', labelAr: 'علبة', labelEn: 'Pack' },
  { value: 'صينية', valueEn: 'Tray', labelAr: 'صينية', labelEn: 'Tray' },
];

// --- Reusable Components ---
const SearchInput = React.memo(({ value, onChange, placeholder, isRtl }: { value: string; onChange: (val: string) => void; placeholder: string; isRtl: boolean }) => (
  <div className="relative group">
    <div className={`absolute ${isRtl ? 'left-3' : 'right-3'} top-1/2 transform -translate-y-1/2  text-gray-400`}>
      <Search className="w-4 h-4" />
    </div>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full ${isRtl ? 'pl-10 pr-2' : 'pr-10 pl-2'} py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white shadow-sm text-xs placeholder-gray-400 ${isRtl ? 'text-right' : 'text-left'}`}
      aria-label={placeholder}
    />
    {value && (
      <button
        onClick={() => onChange('')}
        aria-label={isRtl ? 'مسح البحث' : 'Clear search'}
        className={`absolute ${isRtl ? 'left-5' : 'right-5'} top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-amber-500`}
      >
        <X className="w-4 h-4" />
      </button>
    )}
  </div>
));

const ToggleSwitch = React.memo(({ isActive, onChange }: { isActive: boolean; onChange: () => void }) => (
  <label className="flex items-center cursor-pointer">
    <div className="relative">
      <input type="checkbox" checked={isActive} onChange={onChange} className="sr-only" />
      <div className={`block w-10 h-5 rounded-full ${isActive ? 'bg-green-500' : 'bg-gray-300'}`}></div>
      <div
        className={`absolute left-1 top-1 bg-white w-3 h-3 rounded-full transition-transform ${
          isActive ? 'transform translate-x-5' : ''
        }`}
      ></div>
    </div>
  </label>
));

const ProductCard = React.memo(
  ({
    product,
    isRtl,
    t,
    user,
    onEdit,
    onDelete,
    onToggleActive,
  }: {
    product: Product;
    isRtl: boolean;
    t: typeof translations['ar'];
    user: any;
    onEdit: (p: Product) => void;
    onDelete: (id: string) => void;
    onToggleActive: (id: string) => void;
  }) => {
    const cardBgClass = product.isActive ? 'bg-white' : 'bg-red-200'; // ✅ bg-red-100 when inactive

    return (
      <div
        className={`p-3 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex flex-col relative ${cardBgClass}`}
      >
        <div className="mb-2 h-40 w-full bg-gray-100 rounded-lg overflow-hidden relative">
          {product.image ? (
            <img
              src={product.image}
              alt={product.displayName}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                const target = e.currentTarget;
                target.style.display = 'none';
                target.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-50">
              <ImageIcon className="w-8 h-8 text-gray-300" />
            </div>
          )}
          <div className={`absolute top-1 ${isRtl ? 'left-1' : 'right-1'} z-5`}>
            <div className="bg-white rounded-full p-1 shadow-sm">
              <ToggleSwitch
                isActive={product.isActive}
                onChange={() => onToggleActive(product._id)}
              />
            </div>
          </div>
        </div>

        <div className="flex-1 space-y-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-gray-900 text-xs truncate">{product.displayName}</h3>
            <p className="text-xs text-gray-500">{product.code}</p>
          </div>
          <p className="text-xs text-amber-600 truncate">
            {isRtl ? product.department.name : product.department.nameEn || product.department.name}
          </p>
          <p className="font-medium text-gray-900 text-xs">
            {product.price} {isRtl ? 'ريال' : 'SAR'} / {product.displayUnit}
          </p>
        </div>

        {['admin', 'production'].includes(user?.role ?? '') && (
          <div className={`absolute bottom-2 ${isRtl ? 'left-2.5 ' : 'right-2.5'} flex gap-1`}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(product);
              }}
              className="p-1 w-6 h-6 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center"
              title={t.edit}
            >
              <Edit2 className="w-3 h-3" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(product._id);
              }}
              className="p-1 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center"
              title={t.delete}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    );
  }
);

// --- Main Component ---
export function Products() {
  const { language } = useLanguage();
  const isRtl = language === 'ar';
  const t = translations[isRtl ? 'ar' : 'en'];
  const { user } = useAuth();

  const [products, setProducts] = useState<Product[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingProductId, setDeletingProductId] = useState<string | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    nameEn: '',
    code: '',
    department: '',
    price: '',
    unit: '',
    image: '',
    isActive: true,
  });
  const [imagePreview, setImagePreview] = useState<string>('');

  const debouncedSetSearch = useCallback(
    debounce((value: string) => setSearchTerm(value), 500),
    []
  );

  useEffect(() => {
    debouncedSetSearch(searchInput);
  }, [searchInput, debouncedSetSearch]);

  // ✅ تصفية المنتجات مع الحفاظ على التوافق الفوري
  const filteredProducts = useMemo(() => {
    return products
      .filter(
        (p) =>
          p.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.code.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .filter((p) => !filterDepartment || p.department._id === filterDepartment)
      .filter((p) => {
        if (filterActive === 'active') return p.isActive;
        if (filterActive === 'inactive') return !p.isActive;
        return true;
      });
  }, [products, searchTerm, filterDepartment, filterActive]);

  // ✅ جلب البيانات
  useEffect(() => {
    if (!user || !['admin', 'production'].includes(user.role)) {
      setError(t.unauthorized);
      toast.error(t.unauthorized);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsRes, departmentsRes] = await Promise.all([
          productsAPI.getAll({ limit: 0 }),
          departmentAPI.getAll({ limit: 100 }),
        ]);

        const mappedProducts = (productsRes.data.products || productsRes.data || []).map((p: any) => ({
          ...p,
          displayName: isRtl ? p.name : p.nameEn || p.name,
          displayUnit: isRtl ? p.unit || 'غير محدد' : p.unitEn || p.unit || 'N/A',
          isActive: Boolean(p.isActive),
        }));

        setProducts(mappedProducts);
        setDepartments(departmentsRes.data || []);
        setError('');
      } catch (err: any) {
        const msg = err.response?.data?.message || t.fetchError;
        setError(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, isRtl, t]);

  // ✅ تغيير حالة النشاط مع تحديث فوري + استرجاع في حالة الفشل
  const toggleActive = async (id: string) => {
    const originalProduct = products.find((p) => p._id === id);
    if (!originalProduct) return;

    // ✅ تحديث فوري في الواجهة
    setProducts((prev) =>
      prev.map((p) => (p._id === id ? { ...p, isActive: !p.isActive } : p)) // ✅ صححت _Id إلى _id
    );

    try {
      const newIsActive = !originalProduct.isActive;
      await productsAPI.update(id, { isActive: newIsActive });
      toast.success(newIsActive ? t.updated : t.updated); // يمكنك تغيير الرسالة لاحقًا
    } catch (err) {
      // ✅ استرجاع الحالة الأصلية في حالة الفشل
      setProducts((prev) =>
        prev.map((p) => (p._id === id ? originalProduct : p))
      );
      toast.error(t.saveError);
    }
  };

  const openModal = (product?: Product) => {
    if (!['admin', 'production'].includes(user?.role || '')) {
      toast.error(t.unauthorized);
      return;
    }
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        nameEn: product.nameEn || '',
        code: product.code,
        department: product.department._id,
        price: product.price.toString(),
        unit: product.unit || '',
        image: product.image || '',
        isActive: product.isActive,
      });
      setImagePreview(product.image || '');
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        nameEn: '',
        code: '',
        department: departments[0]?._id || '',
        price: '',
        unit: '',
        image: '',
        isActive: true,
      });
      setImagePreview('');
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
    setError('');
    setImagePreview('');
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toast.error(t.invalidImage);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
      setFormData((prev) => ({ ...prev, image: reader.result as string }));
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImagePreview('');
    setFormData((prev) => ({ ...prev, image: '' }));
    const input = document.getElementById('image-upload') as HTMLInputElement | null;
    if (input) input.value = '';
  };

  const validateForm = (): boolean => {
    const { name, code, department, price } = formData;
    if (!name.trim() || !code.trim() || !department || !price.trim()) {
      toast.error(t.requiredFields);
      return false;
    }
    const parsedPrice = parseFloat(price);
    if (isNaN(parsedPrice) || parsedPrice <= 0) {
      toast.error(t.invalidPrice);
      return false;
    }
    if (formData.unit && !unitOptions.some((u) => u.value === formData.unit)) {
      toast.error(t.invalidUnit);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const productData = {
      name: formData.name.trim(),
      nameEn: formData.nameEn.trim() || undefined,
      code: formData.code.trim(),
      department: formData.department,
      price: parseFloat(formData.price),
      unit: formData.unit || undefined,
      image: formData.image || undefined,
      isActive: formData.isActive,
    };

    try {
      if (editingProduct) {
        const updated = await productsAPI.update(editingProduct._id, productData);
        const mapped = {
          ...updated,
          displayName: isRtl ? updated.name : updated.nameEn || updated.name,
          displayUnit: isRtl ? updated.unit || 'غير محدد' : updated.unitEn || updated.unit || 'N/A',
          isActive: Boolean(updated.isActive),
        };
        setProducts((prev) => prev.map((p) => (p._id === editingProduct._id ? mapped : p)));
        toast.success(t.updated);
      } else {
        const newProd = await productsAPI.create(productData);
        const mapped = {
          ...newProd,
          displayName: isRtl ? newProd.name : newProd.nameEn || newProd.name,
          displayUnit: isRtl ? newProd.unit || 'غير محدد' : newProd.unitEn || newProd.unit || 'N/A',
          isActive: Boolean(newProd.isActive),
        };
        setProducts((prev) => [...prev, mapped]);
        toast.success(t.added);
      }
      closeModal();
    } catch (err: any) {
      const msg = err.response?.data?.message || t.saveError;
      toast.error(msg);
    }
  };

  const openDeleteModal = (id: string) => {
    setDeletingProductId(id);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeletingProductId(null);
  };

  const confirmDelete = async () => {
    if (!deletingProductId || !['admin', 'production'].includes(user?.role || '')) {
      toast.error(t.unauthorized);
      return;
    }

    try {
      await productsAPI.delete(deletingProductId);
      setProducts((prev) => prev.filter((p) => p._id !== deletingProductId));
      toast.success(t.deleted);
      closeDeleteModal();
    } catch (err: any) {
      const msg = err.response?.data?.message || t.deleteError;
      toast.error(msg);
    }
  };

  return (
    <div
      className="mx-auto px-4 py-6 min-h-screen overflow-y-auto"
      dir={isRtl ? 'rtl' : 'ltr'}
      role="main"
      aria-label={t.manage}
    >
      {/* Header */}
      <div className="mb-4 flex flex-col items-center sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="flex  items-center gap-2">
          <div>
            <div className="flex flex-row gap-2 items-center justify-center md:justify-start mb-1">
            <Package className="w-6 h-6 text-amber-600" />
              <h1 className="text-xl font-bold text-gray-900">           
{t.manage}</h1></div>
            
            <p className="text-gray-600 text-xs">
              {isRtl ? 'قم بإضافة المنتجات أو تعديلها أو حذفها' : 'Add, edit, or delete products'}
            </p>
          </div>
        </div>
        {['admin', 'production'].includes(user?.role ?? '') && (
          <button
            onClick={() => openModal()}
            className="w-full w-auto max-w-[180px] px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs transition-colors flex items-center justify-center gap-1.5 shadow-sm"
            aria-label={t.add}
          >
            {t.add}            <Plus className="w-3.5 h-3.5" />

          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-600" />
          <span className="text-red-600 text-xs">{error}</span>
        </div>
      )}

      <div className="space-y-3">
        <div className="p-4 bg-white rounded-xl shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <SearchInput
                value={searchInput}
                onChange={setSearchInput}
                placeholder={t.searchPlaceholder}
                isRtl={isRtl}
              />
            </div>
            <div>
              <CustomDropdown
                value={filterDepartment}
                onChange={setFilterDepartment}
                options={[
                  { value: '', label: t.allDepartments },
                  ...departments.map((d) => ({
                    value: d._id,
                    label: isRtl ? d.name : d.nameEn || d.name,
                  })),
                ]}
                ariaLabel={t.filterDepartment}
              />
            </div>
            <div>
              <CustomDropdown
                value={filterActive}
                onChange={(val) => setFilterActive(val as 'all' | 'active' | 'inactive')}
                options={[
                  { value: 'all', label: isRtl ? 'الكل' : 'All' },
                  { value: 'active', label: t.activeProducts },
                  { value: 'inactive', label: t.inactiveProducts },
                ]}
                ariaLabel={isRtl ? 'تصفية حسب الحالة' : 'Filter by status'}
              />
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-gray-600">
          {isRtl ? `عدد المنتجات: ${filteredProducts.length}` : `Products Count: ${filteredProducts.length}`}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="p-3 bg-white rounded-xl shadow-sm animate-pulse">
                <div className="h-20 bg-gray-200 rounded-lg mb-2"></div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-2 bg-gray-200 rounded w-1/4"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="p-6 text-center bg-white rounded-xl shadow-sm">
            <Package className="w-10 h-10 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 text-xs">
              {searchTerm || filterDepartment || filterActive !== 'all' ? t.noMatch : t.empty}
            </p>
            {['admin', 'production'].includes(user?.role ?? '') &&
              !searchTerm &&
              !filterDepartment &&
              filterActive === 'all' && (
                <button
                  onClick={() => openModal()}
                  className="mt-3 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs transition-colors"
                  aria-label={t.addFirst}
                >
                  {t.addFirst}
                </button>
              )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product._id}
                product={product}
                isRtl={isRtl}
                t={t}
                user={user}
                onEdit={openModal}
                onDelete={openDeleteModal}
                onToggleActive={toggleActive}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={(e) => e.target === e.currentTarget && closeModal()}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {editingProduct ? t.edit : t.add}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t.name}</label>
                    <input
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder={t.namePlaceholder}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t.nameEn}</label>
                    <input
                      value={formData.nameEn}
                      onChange={(e) => setFormData((prev) => ({ ...prev, nameEn: e.target.value }))}
                      placeholder={t.nameEnPlaceholder}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t.code}</label>
                    <input
                      value={formData.code}
                      onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value }))}
                      placeholder={t.codePlaceholder}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t.department}</label>
                    <CustomDropdown
                      value={formData.department}
                      onChange={(val) => setFormData((prev) => ({ ...prev, department: val }))}
                      options={[
                        { value: '', label: t.departmentPlaceholder },
                        ...departments.map((d) => ({
                          value: d._id,
                          label: isRtl ? d.name : d.nameEn || d.name,
                        })),
                      ]}
                      ariaLabel={t.department}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t.price}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData((prev) => ({ ...prev, price: e.target.value }))}
                      placeholder="0.00"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{t.unit}</label>
                    <CustomDropdown
                      value={formData.unit}
                      onChange={(val) => setFormData((prev) => ({ ...prev, unit: val }))}
                      options={[
                        { value: '', label: t.unitPlaceholder },
                        ...unitOptions.map((opt) => ({
                          value: opt.value,
                          label: isRtl ? opt.labelAr : opt.labelEn,
                        })),
                      ]}
                      ariaLabel={t.unit}
                    />
                  </div>
                </div>

                <div className="py-2 flex items-center">
                  <ToggleSwitch
                    isActive={formData.isActive}
                    onChange={() => setFormData((prev) => ({ ...prev, isActive: !prev.isActive }))}
                  />
                  <span className="mr-3 text-xs font-medium text-gray-700">{t.active}</span>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">{t.image}</label>
                  <div className="space-y-2">
                    {imagePreview ? (
                      <div className="relative">
                        <img src={imagePreview} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                          title={isRtl ? 'إزالة الصورة' : 'Remove image'}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : (
                      <label
                        htmlFor="image-upload"
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                      >
                        <Upload className="w-6 h-6 text-gray-400 mb-1" />
                        <span className="text-xs text-gray-500">{t.imagePlaceholder}</span>
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                </div>

                {error && (
                  <div className="p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <span className="text-red-600 text-xs">{error}</span>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-xs"
                  >
                    {t.cancel}
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs"
                  >
                    {editingProduct ? t.update : t.add}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {isDeleteModalOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={(e) => e.target === e.currentTarget && closeDeleteModal()}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{t.confirmDelete}</h3>
              <p className="text-xs text-gray-600 mb-4">{t.deleteWarning}</p>
              {error && (
                <div className="p-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 mb-4">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="text-red-600 text-xs">{error}</span>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  onClick={closeDeleteModal}
                  className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg text-xs"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs"
                >
                  {t.delete}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}